import {
  Timestamp,
  addDoc,
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where
} from 'firebase/firestore';
import { getFirebase, isFirebaseConfigured } from '@/config/firebase';
import type { LeaderboardEntry } from '@/types/models';

const COLLECTION = 'leaderboard';

export type LeaderboardScope = 'allTime' | 'weekly';

export const leaderboardService = {
  async submit(entry: Omit<LeaderboardEntry, 'achievedAt' | 'id'>): Promise<string | null> {
    if (!isFirebaseConfigured()) return null;
    try {
      const { db } = getFirebase();
      const ref = await addDoc(collection(db, COLLECTION), {
        ...entry,
        achievedAt: Timestamp.now()
      });
      return ref.id;
    } catch (err) {
      console.error('leaderboard submit failed', err);
      return null;
    }
  },

  async top(scope: LeaderboardScope = 'allTime', count = 100): Promise<LeaderboardEntry[]> {
    if (!isFirebaseConfigured()) return [];
    try {
      const { db } = getFirebase();

      if (scope === 'weekly') {
        // Evitamos el índice compuesto: bajamos los de la semana ordenados por fecha
        // y los ordenamos por score en memoria.
        const weekAgo = Timestamp.fromMillis(Date.now() - 7 * 24 * 60 * 60 * 1000);
        const q = query(
          collection(db, COLLECTION),
          where('achievedAt', '>=', weekAgo),
          orderBy('achievedAt', 'desc'),
          limit(Math.max(count, 200))
        );
        const snap = await getDocs(q);
        const all = snap.docs.map(mapDoc);
        all.sort((a, b) => b.score - a.score);
        return all.slice(0, count);
      }

      const q = query(collection(db, COLLECTION), orderBy('score', 'desc'), limit(count));
      const snap = await getDocs(q);
      return snap.docs.map(mapDoc);
    } catch (err) {
      console.error('leaderboard top failed', err);
      return [];
    }
  }
};

function mapDoc(d: import('firebase/firestore').QueryDocumentSnapshot): LeaderboardEntry {
  const raw = d.data() as Omit<LeaderboardEntry, 'id' | 'achievedAt'> & {
    achievedAt: Timestamp;
  };
  return {
    id: d.id,
    userId: raw.userId,
    displayName: raw.displayName,
    score: raw.score,
    level: raw.level,
    durationSeconds: raw.durationSeconds,
    isAnonymous: raw.isAnonymous,
    achievedAt: raw.achievedAt.toMillis()
  };
}
